import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { track } from '@/lib/analytics';
import { useAuthStore } from '@/lib/auth';
import { initialsOf } from '@/lib/initials';
import { BrandLogo } from '@/components/BrandLogo';

/**
 * THE unified fantasy.draftedge.com header (spec: UI pass 2026-08-07).
 *
 * This component must stay PIXEL-EQUIVALENT to the PHP partial
 * fantasyapp/static/site-header.php + assets/css/de-header.css that renders
 * the same header on the landing page, privacy/contact, and both rankings
 * pages. Every literal here (64px bar, 320px panel, #F2C14E accent,
 * rgba(12,15,26,0.78) glass, 280ms cubic-bezier(0.32,0.72,0,1)) mirrors a
 * token in that CSS. Change one side only with the other in the same commit.
 *
 * Owner requirements: hamburger on the RIGHT, drawer slides in FROM THE
 * RIGHT (not a top dropdown), Sign in + the paid CTA visible in the bar.
 *
 * Hidden on the focused draft surfaces (/drafts/new, /drafts/:id) which run
 * their own full-screen chrome.
 */

const DRAFT_LINKS = [
  { to: '/drafts', label: 'Drafts' },
  { to: '/cheatsheet', label: 'Cheat Sheets' },
  { to: '/mocks', label: 'Mock Drafts' },
  { to: '/compare', label: 'Compare Players' },
];

const RESEARCH_LINKS: Array<{ to: string; label: string; external?: boolean; authExt?: string }> = [
  // Auth-aware (owner 2026-08-08 refined): anonymous -> the public pages,
  // members -> the in-app tool. authExt carries the anonymous destination.
  { to: '/rankings', label: 'Rankings', authExt: '/draft-rankings/' },
  { to: '/rookies', label: 'Rookies', authExt: '/rookie-rankings/' },
  // PHP page outside the router; external:true renders an <a> so the
  // browser leaves the SPA instead of client-routing into a 404.
  { to: '/nfl-schedule-strength/', label: 'Schedule Strength', external: true },
  { to: '/trending', label: 'Waiver Wire' },
  { to: '/exposure', label: 'Exposure' },
  { to: '/depth-charts', label: 'Depth Charts' },
  { to: '/injuries', label: 'Injuries' },
  { to: '/news', label: 'News' },
];

const KIT_URL = 'https://draftedge.com/register/?plan=draft_kit';

export function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const burgerRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Route change closes the drawer (it would otherwise sit over the new page).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Scroll lock while open; gutter kept stable so desktop layout cannot shift.
  useEffect(() => {
    const root = document.documentElement;
    if (open) {
      root.style.overflow = 'hidden';
      root.style.scrollbarGutter = 'stable';
    } else {
      root.style.overflow = '';
      root.style.scrollbarGutter = '';
    }
    return () => {
      root.style.overflow = '';
      root.style.scrollbarGutter = '';
    };
  }, [open]);

  // Closed panel must be untabbable and invisible to AT even though it stays
  // mounted for the slide animation. `inert` handles both.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (open) {
      el.removeAttribute('inert');
      closeRef.current?.focus();
    } else {
      el.setAttribute('inert', '');
    }
  }, [open]);

  // Escape closes; Tab cycles inside the panel (focus trap) while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        burgerRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const f = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
      ).filter((el) => el.offsetParent !== null);
      const first = f[0];
      const last = f[f.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (/^\/drafts\/.+/.test(location.pathname)) {
    return null;
  }

  const closeAndRefocus = () => {
    setOpen(false);
    burgerRef.current?.focus();
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout(); // optimistic: clears auth state synchronously first
    navigate('/', { replace: true });
  };

  const item = (to: string, label: string, external?: boolean) => {
    if (external) {
      return (
        <a
          key={to}
          href={to}
          className="flex h-11 items-center rounded-lg px-3 font-sans text-[15px] font-medium text-[#E3E9F2] transition-colors hover:bg-white/[0.06]"
        >
          {label}
        </a>
      );
    }
    const active = location.pathname === to || location.pathname.startsWith(`${to}/`);
    return (
      <Link
        key={to}
        to={to}
        aria-current={active ? 'page' : undefined}
        className={`flex h-11 items-center rounded-lg px-3 font-sans text-[15px] transition-colors ${
          active
            ? 'bg-[rgba(242,193,78,0.10)] font-semibold text-[#F2C14E]'
            : 'font-medium text-[#E3E9F2] hover:bg-white/[0.06]'
        }`}
      >
        {label}
      </Link>
    );
  };

  const groupLabel = (text: string) => (
    <p className="mb-1 mt-5 px-3 font-sans text-[11px] font-semibold uppercase tracking-[0.08em] text-white/60 first:mt-2">
      {text}
    </p>
  );

  // Shared geometry for the two bar buttons (spec section 2 collapse map).
  const ghostCls =
    'inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg border border-white/[0.14] px-[18px] font-sans text-[15px] font-semibold text-[#E3E9F2] transition-colors hover:bg-white/[0.06] max-[767px]:px-3.5 max-[767px]:text-sm max-[389px]:border-0 max-[389px]:px-1.5';
  const ctaCls =
    'inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg bg-[#F2C14E] px-4 font-sans text-sm font-semibold text-[#1F1503] transition-[filter] hover:brightness-105 max-[767px]:h-[38px] max-[767px]:px-3.5 max-[389px]:px-3';

  return (
    <>
      <header className="sticky top-0 z-[900] border-b border-white/[0.08] bg-[#151929] supports-[backdrop-filter]:bg-[rgba(12,15,26,0.78)] supports-[backdrop-filter]:backdrop-blur-[14px] supports-[backdrop-filter]:backdrop-saturate-[1.4]">
        <div className="mx-auto flex h-[64px] max-w-[1180px] items-center justify-between px-6 max-[1023px]:px-5 max-[767px]:px-[18px] max-[559px]:px-4 max-[389px]:px-3">
          <Link to="/" aria-label="DraftEdge home" className="flex flex-none items-center">
            <BrandLogo className="h-7 max-[559px]:h-[22px]" />
          </Link>

          <div className="flex items-center gap-2.5 max-[559px]:gap-2 max-[389px]:gap-1.5">
            {isBootstrapping ? (
              <div className="h-10 w-24" aria-hidden="true" />
            ) : isAuthenticated ? (
              <>
                <Link to="/dashboard" className={ghostCls}>
                  Dashboard
                </Link>
                <Link
                  to="/account"
                  title="Account settings"
                  className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-brand-primary font-sans text-xs font-semibold uppercase text-white transition hover:opacity-90"
                >
                  {initialsOf(user?.first_name, user?.last_name, user?.email)}
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className={ghostCls}>
                  Sign in
                </Link>
                <a
                  href={KIT_URL}
                  onClick={() => track('cta_click', { cta_location: 'header' })}
                  className={ctaCls}
                >
                  Get the Kit
                </a>
              </>
            )}
            <button
              ref={burgerRef}
              type="button"
              aria-label="Menu"
              aria-expanded={open}
              aria-controls="site-menu"
              onClick={() => setOpen(true)}
              className="flex h-11 w-11 flex-none items-center justify-center rounded-lg text-[#E3E9F2] transition-colors hover:bg-white/[0.06]"
            >
              <svg viewBox="0 0 20 14" className="h-3.5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                <path d="M1 1h18M1 7h18M1 13h18" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Backdrop: fades, click closes. */}
      <div
        aria-hidden="true"
        onClick={closeAndRefocus}
        className={`fixed inset-0 z-[990] bg-black/55 transition-opacity duration-[240ms] motion-reduce:duration-[120ms] ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      {/* Drawer: slides in from the RIGHT. Stays mounted for the animation;
          inert while closed. */}
      <div
        ref={panelRef}
        id="site-menu-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Site menu"
        className={`fixed right-0 top-0 z-[1000] flex h-dvh w-[320px] max-w-[85vw] flex-col overflow-y-auto overscroll-contain border-l border-white/[0.08] bg-[#151929] px-5 pb-5 pt-3 shadow-[-24px_0_48px_rgba(0,0,0,0.5)] transition-transform motion-reduce:transition-opacity ${
          open
            ? 'translate-x-0 duration-[280ms] ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:opacity-100'
            : 'translate-x-[102%] duration-200 ease-in motion-reduce:translate-x-0 motion-reduce:opacity-0'
        }`}
      >
        <div className="-mr-2 flex h-[52px] flex-none items-center justify-end">
          <button
            ref={closeRef}
            type="button"
            aria-label="Close menu"
            onClick={closeAndRefocus}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-[#E3E9F2] transition-colors hover:bg-white/[0.06]"
          >
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
              <path d="M3 3l14 14M17 3L3 17" />
            </svg>
          </button>
        </div>

        <nav id="site-menu" aria-label="Site menu">
          {groupLabel('Draft tools')}
          {DRAFT_LINKS.map((l) => item(l.to, l.label))}
          {groupLabel('Research')}
          {RESEARCH_LINKS.map((l) =>
            l.authExt && !isAuthenticated
              ? item(l.authExt, l.label, true)
              : item(l.to, l.label, l.external)
          )}
          {item('/pricing', 'Pricing')}
        </nav>

        <div className="mt-auto flex flex-col gap-2.5 border-t border-white/[0.08] pt-4">
          {isAuthenticated ? (
            <>
              <Link
                to="/dashboard"
                className="flex h-12 items-center justify-center rounded-lg bg-[#F2C14E] font-sans text-[15px] font-semibold text-[#1F1503] transition-[filter] hover:brightness-105"
              >
                Dashboard
              </Link>
              <Link
                to="/account"
                className="flex h-11 items-center justify-center rounded-lg border border-white/[0.14] font-sans text-[15px] font-semibold text-[#E3E9F2] transition-colors hover:bg-white/[0.06]"
              >
                Account
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="flex h-11 items-center justify-center font-sans text-sm font-medium text-white/60 transition-colors hover:text-[#E3E9F2]"
              >
                Log out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="flex h-11 items-center justify-center rounded-lg border border-white/[0.14] font-sans text-[15px] font-semibold text-[#E3E9F2] transition-colors hover:bg-white/[0.06]"
              >
                Sign in
              </Link>
              <a
                href={KIT_URL}
                onClick={() => track('cta_click', { cta_location: 'header_menu' })}
                className="flex h-12 items-center justify-center rounded-lg bg-[#F2C14E] font-sans text-[15px] font-semibold text-[#1F1503] transition-[filter] hover:brightness-105"
              >
                Get the Kit
              </a>
            </>
          )}
        </div>
      </div>
    </>
  );
}
